import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Col,
  DatePicker,
  Row,
  Select,
} from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { DuplicateCardsHelpers, FormHelpers } from 'ontraccr-common';

import { toTitleCase, getIdMap } from '../../../helpers/helpers';
import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';
import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import Colors from '../../../constants/Colors';
import BorderlessButton from '../../../common/buttons/BorderlessButton';
import OnTraccrCheckbox from '../../../common/inputs/OnTraccrCheckbox';
import OnTraccrRadioGroup from '../../../common/inputs/OnTraccrRadioGroup';

import { formatProjectLabelFromCompanySettings } from '../../../projects/projectHelpers';
import { formatEquipmentLabelFromCompanySettings } from '../../../equipment/equipment.helpers';
import BoardColorOption from '../../../boards/BoardColorOption';
import RelativeDateInput, { DEFAULT_RELATIVE_DATE } from './RelativeDateInput';

const validFieldTypes = new Set([
  'text',
  'calculation',
  'attribute',
  'yes-no',
  'dropdown',
  'table',
  'dateTime',
  'dateRange',
]);

const possibleNumericalFieldTypes = new Set([
  'calculation',
  'attribute',
  'text',
]);

const possibleDateFieldTypes = new Set([
  'dateTime',
  'dateRange',
]);

const validDropdownTypes = new Set([
  'Customers',
  'Custom',
  'Projects',
  'Users',
  'Costcodes',
  'Vendors',
  'Equipment',
  'Forms',
  'Labels',
  'Contacts',
  'Buckets',
  'ScheduleCategories',
  'Divisions',
  'Boards',
]);

const dateOperators = [
  {
    value: 'isBefore',
    label: 'Before',
  },
  {
    value: 'isAfter',
    label: 'After',
  },
  {
    value: 'within',
    label: 'Within',
  },
];

export const dateOperatorSet = new Set([
  'isBefore',
  'isAfter',
  'within',
]);

const validTableTypes = new Set([
  'TimeEntry',
  'Equipment',
]);

const { supportsCaseInsensitiveSearch } = FormHelpers;
const { getRelativeDateFormulaValue } = DuplicateCardsHelpers;

const DATE_MODES = { FIXED: 'fixed', RELATIVE: 'relative' };
const DATE_MODE_OPTIONS = [
  { value: DATE_MODES.FIXED, label: 'Fixed date' },
  { value: DATE_MODES.RELATIVE, label: 'Relative' },
];

const isEqualsOp = (op) => op === '=' || op === '=-dropdown';
const isIncludesOp = (op) => op === 'has' || op === 'has-dropdown';
const isNotEqualsOp = (op) => op === '!=' || op === '!=-dropdown';
const isNotIncludesOp = (op) => op === '!has' || op === '!has-dropdown';

/**
 * When switching Equals / Does not equal → Includes / Does not include,
 * keep the value when possible. For dropdown, map selected IDs to labels for the text input.
 */
const getValueWhenOperatorChanges = ({
  previousOperator,
  newOperator,
  previousValue,
  fieldSelectedType,
  dataType,
  subDataType,
  getDropdownData,
}) => {
  const fromEqualsFamily = isEqualsOp(previousOperator) || isNotEqualsOp(previousOperator);
  const toIncludesFamily = isIncludesOp(newOperator) || isNotIncludesOp(newOperator);

  if (!fromEqualsFamily || !toIncludesFamily) return null;

  if (fieldSelectedType === 'dropdown') {
    const filter = dataType === 'Buckets'
      ? { filterKey: 'bucketTemplateId', filterValue: subDataType }
      : {};
    const dropdownData = getDropdownData(dataType, filter);
    const selectedIds = Array.isArray(previousValue) ? previousValue : [];
    const idSet = new Set(selectedIds);
    return dropdownData
      .filter(({ id: optionId }) => idSet.has(optionId))
      .map(({ name: optionName }) => optionName)
      .join(', ');
  }

  return previousValue;
};

const getTimeEntryTableFields = (t, field) => {
  if (!field) return [];
  const { name, id } = field;
  return [
    {
      ...field,
      id: `${id}.total_hours`,
      name: `${name}: Any Entry Total Hours`,
      selectedType: 'text',
    },
    {
      ...field,
      id: `${id}.work_hours`,
      name: `${name}: Any Entry Work Hours`,
      selectedType: 'text',
    },
    {
      ...field,
      id: `${id}.break_hours`,
      name: `${name}: Any Entry Break Hours`,
      selectedType: 'text',
    },
    {
      ...field,
      id: `${id}.projectId`,
      name: `${name}: Any Entry ${t('Project')}`,
      selectedType: 'dropdown',
      configProps: {
        dataType: 'Projects',
      },
    },
    {
      ...field,
      id: `${id}.costcodeId`,
      name: `${name}: Any Entry Cost Code`,
      selectedType: 'dropdown',
      configProps: {
        dataType: 'Costcodes',
      },
    },
    {
      ...field,
      id: `${id}.costcode_code`,
      name: `${name}: Any Entry Cost Code - Code`,
      selectedType: 'text',
    },
    {
      ...field,
      id: `${id}.costcode_name`,
      name: `${name}: Any Entry Cost Code - Name`,
      selectedType: 'text',
    },
  ];
};

const getAdvancedSearchTimeEntryFields = (field) => {
  if (!field) return [];
  const { name, id } = field;
  return [
    {
      ...field,
      id: `${id}.mostRecentDate`,
      name: `${name}: Most Recent Day Worked`,
      selectedType: 'dateTime',
    },
  ];
};

const getEquipmentTableFields = (t, field) => {
  if (!field) return [];
  const { name, id } = field;
  return [
    {
      ...field,
      id: `${id}.equipmentId`,
      name: `${name}: Any Equipment`,
      selectedType: 'dropdown',
      configProps: {
        dataType: 'Equipment',
      },
    },
    {
      ...field,
      id: `${id}.name`,
      name: `${name}: Any Row Name`,
      selectedType: 'text',
    },
    {
      ...field,
      id: `${id}.code`,
      name: `${name}: Any Row ID`,
      selectedType: 'text',
    },
    {
      ...field,
      id: `${id}.hours`,
      name: `${name}: Any Row Hours`,
      selectedType: 'text',
    },
    {
      ...field,
      id: `${id}.mileage`,
      name: `${name}: Any Row Mileage`,
      selectedType: 'text',
    },
    {
      ...field,
      id: `${id}.currentProjectId`,
      name: `${name}: Any Row Location`,
      selectedType: 'dropdown',
      configProps: {
        dataType: 'Projects',
      },
    },
    {
      ...field,
      id: `${id}.toProjectId`,
      name: `${name}: Any Row To Location`,
      selectedType: 'dropdown',
      configProps: {
        dataType: 'Projects',
      },
    },
    {
      ...field,
      id: `${id}.costcodeId`,
      name: `${name}: Any Row Cost Code`,
      selectedType: 'dropdown',
      configProps: {
        dataType: 'Costcodes',
      },
    },
    {
      ...field,
      id: `${id}.costcode`,
      name: `${name}: Any Row Cost Code - Name`,
      selectedType: 'text',
    },
    {
      ...field,
      id: `${id}.vendorId`,
      name: `${name}: Any Row Vendor`,
      selectedType: 'dropdown',
      configProps: {
        dataType: 'Vendors',
      },
    },
  ];
};

export default function ConditionalRenderingBuilder({
  sections = [],
  id,
  onChange,
  conditionalRenderingFormula = {},
  customers = [],
  projects = [],
  users = [],
  costcodes = [],
  phases = [],
  projectIdMap = {},
  vendors = [],
  equipment = [],
  formTemplates = [],
  labels = [],
  contactAddressBooks = {},
  buckets = [],
  scheduleCategories = [],
  formStatuses = [],
  boardStatuses = [],
  boardColorLegend = [],
  showDelete = false,
  showAdd = false,
  onAddClicked,
  onDeleteClicked,
  isSection = false,
  additionalFields = [],
  isAdvancedSearch = false,
  materials = [],
  relativeDateAnchors, // Only conflict detection passes this
}) {
  const { t } = useTranslation();

  const { settings = {} } = useSelector((state) => state.settings.company);
  const divisions = useSelector((state) => state.settings.divisions);
  const boardsForDropdown = useSelector(
    (state) => state.boards.boardsForDropdown,
  );

  const [selectedField, setSelectedField] = useState();
  const [selectedOperator, setSelectedOperator] = useState('=');
  const [selectedValue, setSelectedValue] = useState();
  const [selectedType, setSelectedType] = useState();
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [selectedRelativeDate, setSelectedRelativeDate] = useState();

  useEffect(() => {
    const {
      field,
      operator,
      value,
      type,
      analytics,
      relativeDate,
    } = conditionalRenderingFormula ?? {};
    setSelectedField(field);
    setSelectedOperator(operator);
    setSelectedValue(value);
    setSelectedType(type);
    setAnalyticsEnabled(analytics);
    setSelectedRelativeDate(relativeDate);
  }, [conditionalRenderingFormula]);

  const flatFields = useMemo(() => {
    const relevantFields = [];

    sections.forEach(({ fields: sectionFields = [], name, id: sId }) => (
      // Include same table for calculation columns
      sectionFields
        .filter(({
          id: fieldId,
          selectedType: fieldSelectedType,
          configProps: {
            dataType,
          } = {},
        }) => (
          fieldId !== id
          && validFieldTypes.has(fieldSelectedType)
          && (fieldSelectedType !== 'dropdown' || validDropdownTypes.has(dataType))
          && (fieldSelectedType !== 'table' || validTableTypes.has(dataType))
        ))
        .forEach((field = {}) => {
          const { configProps: { title } = {}, selectedType: fieldSelectedType } = field;
          const formattedName = name ? `${name} - ${title}` : title;

          if (fieldSelectedType === 'table') {
            const fieldWithMeta = { ...field, name: formattedName, sectionId: sId };
            const { configProps: { dataType: tableDataType } = {} } = field;
            if (tableDataType === 'Equipment') {
              relevantFields.push(...getEquipmentTableFields(t, fieldWithMeta));
              return;
            }
            if (isAdvancedSearch) {
              relevantFields.push(...getAdvancedSearchTimeEntryFields(fieldWithMeta));
            } else {
              relevantFields.push(...getTimeEntryTableFields(t, fieldWithMeta));
            }
            return;
          }

          relevantFields.push({ ...field, name: formattedName, sectionId: sId });
        })
    ));
    return additionalFields.concat(relevantFields);
  }, [sections, id, additionalFields, isAdvancedSearch, t]);

  const flatFieldsMap = useMemo(() => getIdMap(flatFields ?? []), [flatFields]);
  const selectedFieldValue = flatFieldsMap[selectedField];

  const costcodePhaseNames = useMemo(() => {
    const map = {};
    phases.forEach((phase) => {
      if (!phase?.costcodeId || !phase.projectId || !phase.name) return;
      const key = `${phase.projectId}.${phase.costcodeId}`;
      if (!map[key]) map[key] = phase.name;
    });
    return map;
  }, [phases]);

  const formatCostcodeName = useCallback((costcode) => {
    const {
      projectId,
      name,
      id,
    } = costcode;
    if (!projectId) return `Global - ${name}`;
    const project = projectIdMap[projectId];
    // Honour the company's project label format so this matches the Projects dropdown.
    const projectLabel = project
      ? formatProjectLabelFromCompanySettings({
        name: project.name,
        number: project.number,
        settings,
      })
      : 'Global';
    const phaseName = costcodePhaseNames[`${projectId}.${id}`] || 'Unphased';
    return `${projectLabel} - ${phaseName} - ${name}`;
  }, [projectIdMap, costcodePhaseNames, settings]);

  const getDropdownData = useCallback((dataType, { filterKey, filterValue }) => {
    switch (dataType) {
      case 'Customers':
        return customers.filter((c) => c.active);
      case 'Projects':
        return projects
          .filter((p) => p.active)
          .map((p) => ({
            id: p.id,
            name: formatProjectLabelFromCompanySettings({
              name: p.name,
              number: p.number,
              settings,
            }),
          }));
      case 'Users':
        return users.filter((u) => u.active);
      case 'Costcodes':
        return costcodes
          .filter((c) => c.active)
          .map((c) => ({
            id: c.id,
            projectId: c.projectId,
            name: formatCostcodeName(c),
          }))
          .sort((a, b) => (
            Number(!b.projectId) - Number(!a.projectId)
            || a.name.localeCompare(b.name)
          ));
      case 'Vendors':
        return vendors.filter((v) => v.active);
      case 'Equipment':
        return equipment
          .filter((e) => e.active)
          .map((e) => ({
            id: e.id,
            name: formatEquipmentLabelFromCompanySettings({
              name: e.name,
              code: e.code,
              settings,
            }),
          }));
      case 'Forms':
        return formTemplates.filter((f) => f.active);
      case 'Labels':
        return labels.map((label) => ({ id: label.id, name: label.title }));
      case 'Contacts':
        return Object.values(contactAddressBooks).flat();
      case 'Custom':
        return (
          selectedFieldValue?.configProps?.customOptions || []
        );
      case 'Buckets':
        return buckets.filter((b) => !filterKey || !filterValue || b[filterKey] === filterValue);
      case 'ScheduleCategories':
        return scheduleCategories;
      case 'FormStatuses':
        return formStatuses.map((status) => ({ id: status.id, name: status.status }));
      case 'BoardStatuses':
        return boardStatuses.map((status) => ({ id: status.value, name: status.label }));
      case 'BoardColorLegend':
        return boardColorLegend.map((entry) => ({
          id: entry.value,
          name: entry.label,
          color: entry.color,
        }));
      case 'Materials':
        return materials?.filter((m) => m.active) ?? [];
      case 'Divisions':
        return Object.values(divisions || {})
          .map((division) => ({ id: division.id, name: division.name }));
      case 'Boards':
        return boardsForDropdown.map((board) => ({ id: board.id, name: board.title }));
      default:
        return [];
    }
  }, [
    selectedField,
    selectedFieldValue,
    customers,
    projects,
    users,
    costcodes,
    vendors,
    equipment,
    formTemplates,
    labels,
    contactAddressBooks,
    buckets,
    scheduleCategories,
    formStatuses,
    boardStatuses,
    boardColorLegend,
    materials,
    divisions,
    formatCostcodeName,
    settings,
    boardsForDropdown,
  ]);

  const possibleOperators = useMemo(() => {
    const { selectedType } = selectedFieldValue || {};
    const equalityTerm = selectedType === 'dropdown' && !isAdvancedSearch ? 'include' : 'equal';
    const baseOperators = selectedField !== 'cardId' ? [
      {
        value: '=',
        label: `${toTitleCase(equalityTerm)}s`,
      },
      {
        value: '!=',
        label: `Does not ${equalityTerm}`,
      },
    ] : [];

    if (possibleDateFieldTypes.has(selectedType)) return dateOperators;
    if (selectedType === 'yes-no') return baseOperators;
    if (selectedType === 'dropdown') {
      const suffix = isAdvancedSearch ? '-dropdown' : '';
      return [
        ...baseOperators.map(({ value, label }) => ({
          value: `${value}${suffix}`,
          label,
        })),
        // Only supported by advanced search
        ...(isAdvancedSearch ? [
          {
            value: 'has-dropdown',
            label: 'Includes',
          },
          {
            value: '!has-dropdown',
            label: 'Does not include',
          },
        ] : []),
      ];
    }

    if (selectedField !== 'number') {
      baseOperators.push(
        {
          value: 'has',
          label: 'Includes',
        },
        {
          value: '!has',
          label: 'Does not include',
        },
      );
    }

    if (!isAdvancedSearch) {
      baseOperators.push(
        {
          value: 'contains',
          label: 'Contains',
        },
        {
          value: '!contains',
          label: 'Does not contain',
        },
      );
    }

    if (
      possibleNumericalFieldTypes.has(selectedType)
      && selectedField !== 'formNumber'
      && (!isAdvancedSearch || !selectedField.startsWith('field-'))
    ) {
      baseOperators.push(
        {
          value: '>',
          label: 'Greater than',
        },
        {
          value: '>=',
          label: 'Greater than or equal to',
        },
        {
          value: '<',
          label: 'Less than',
        },
        {
          value: '<=',
          label: 'Less than or equal to',
        },
      );
    }

    return baseOperators;
  }, [selectedField, selectedFieldValue, isAdvancedSearch]);

  const getFieldType = useCallback((fieldId) => (
    flatFieldsMap[fieldId]?.selectedType
  ), [flatFieldsMap]);

  const currentFieldType = selectedFieldValue?.selectedType;

  const canUseRelativeDate = !!relativeDateAnchors && currentFieldType === 'dateTime';
  const isRelativeDate = canUseRelativeDate && !!selectedRelativeDate;
  const fieldAnchor = relativeDateAnchors?.[selectedField];

  // Value is always the evaluated absolute date so search and completeness checks work unchanged
  const onRelativeDateChange = useCallback((relativeDate, operator = selectedOperator) => {
    const value = getRelativeDateFormulaValue({
      operator,
      relativeDate,
      anchorResponse: fieldAnchor?.response,
    });
    setSelectedOperator(operator);
    setSelectedValue(value);
    setSelectedRelativeDate(relativeDate);
    onChange({
      field: selectedField,
      operator,
      value,
      type: selectedType,
      analytics: null,
      relativeDate,
    });
  }, [fieldAnchor, selectedField, selectedType, selectedOperator, onChange]);

  const onDateModeChange = useCallback((mode) => {
    if (mode === DATE_MODES.RELATIVE) {
      onRelativeDateChange(DEFAULT_RELATIVE_DATE);
      return;
    }
    setSelectedRelativeDate(undefined);
    setSelectedValue(null);
    onChange({
      field: selectedField,
      operator: selectedOperator,
      value: null,
      type: selectedType,
      analytics: null,
    });
  }, [onRelativeDateChange, selectedField, selectedOperator, selectedType, onChange]);

  const canIgnoreCase = useCallback((fieldId, operator) => (
    isAdvancedSearch
    && supportsCaseInsensitiveSearch({ fieldId, fieldType: getFieldType(fieldId), operator })
  ), [isAdvancedSearch, getFieldType]);

  // Carries the flag only while the field and operator still support it.
  const withCaseInsensitive = useCallback((nextFormula, enabled) => (
    enabled && canIgnoreCase(nextFormula.field, nextFormula.operator)
      ? { ...nextFormula, caseInsensitive: true }
      : nextFormula
  ), [canIgnoreCase]);

  const isCaseInsensitive = !!conditionalRenderingFormula?.caseInsensitive;

  const onSelectedValueChange = useCallback((newValue) => {
    setSelectedValue(newValue);
    onChange(withCaseInsensitive({
      field: selectedField,
      operator: selectedOperator,
      value: newValue,
      type: selectedType,
      analytics: null,
    }, isCaseInsensitive));
  }, [
    selectedField,
    selectedOperator,
    onChange,
    selectedType,
    withCaseInsensitive,
    isCaseInsensitive,
  ]);

  const onSelectedOperatorChange = (newOperator) => {
    if (isRelativeDate) {
      onRelativeDateChange(selectedRelativeDate, newOperator);
      return;
    }
    const field = selectedFieldValue || {};
    const { selectedType: fieldSelectedType, configProps: { dataType, subDataType } = {} } = field;

    const nextValue = getValueWhenOperatorChanges({
      previousOperator: selectedOperator,
      newOperator,
      previousValue: selectedValue,
      fieldSelectedType,
      dataType,
      subDataType,
      getDropdownData,
    });

    setSelectedOperator(newOperator);
    setSelectedValue(nextValue);
    onChange(withCaseInsensitive({
      field: selectedField,
      operator: newOperator,
      value: nextValue,
      type: selectedType,
      analytics: null,
    }, isCaseInsensitive));
  };

  const onCaseInsensitiveChange = useCallback((checked) => {
    const { caseInsensitive, ...rest } = conditionalRenderingFormula ?? {};
    onChange(withCaseInsensitive(rest, checked));
  }, [onChange, conditionalRenderingFormula, withCaseInsensitive]);

  const fixedDateInput = useMemo(() => {
    if (!possibleDateFieldTypes.has(currentFieldType)) return null;
    if (selectedOperator === 'within') {
      const [startValue, endValue] = Array.isArray(selectedValue) ? selectedValue : [];
      const relevantValue = startValue && endValue
        ? [moment(startValue), moment(endValue)]
        : null;

      return (
        <DatePicker.RangePicker
          style={{ width: 350 }}
          value={relevantValue}
          onChange={(dates) => {
            const [start, end] = dates || [];
            onSelectedValueChange([
              start ? start.startOf('day').valueOf() : null,
              end ? end.endOf('day').valueOf() : null,
            ]);
          }}
        />
      );
    }

    const relevantValue = selectedValue && !!parseInt(selectedValue, 10)
      ? moment(selectedValue)
      : null;

    const mutatorFunction = selectedOperator === 'isBefore' ? 'startOf' : 'endOf';

    return (
      <DatePicker
        style={{ width: 350 }}
        value={relevantValue}
        onChange={(date) => onSelectedValueChange(date ? date[mutatorFunction]('day').valueOf() : null)}
      />
    );
  }, [currentFieldType, selectedOperator, selectedValue, onSelectedValueChange]);

  const ValueInput = useMemo(() => {
    const {
      selectedType: fieldSelectedType,
      configProps: {
        dataType,
        subDataType,
      } = {},
    } = selectedFieldValue || {};
    if (!fieldSelectedType) return null;
    switch (fieldSelectedType) {
      case 'yes-no':
        return (
          <Select
            style={{ width: 350 }}
            value={selectedValue}
            onChange={onSelectedValueChange}
            disabled={analyticsEnabled}
          >
            <Select.Option value="yes">Yes</Select.Option>
            <Select.Option value="no">No</Select.Option>
            <Select.Option value="n/a">N/A</Select.Option>
          </Select>
        );
      case 'dateTime':
      case 'dateRange':
        // With relative dates the mode toggle and date inputs get their own row
        return canUseRelativeDate ? null : fixedDateInput;
      case 'dropdown': {
        // If the operator is not has or !has, show the dropdown
        // Otherwise, show the text input
        if (!selectedOperator?.includes('has')) {
          const filter = dataType === 'Buckets' ? { filterKey: 'bucketTemplateId', filterValue: subDataType } : {};
          const dropdownData = getDropdownData(dataType, filter);
          return (
            <Select
              style={{ width: 350 }}
              value={selectedValue || []}
              allowClear
              onChange={onSelectedValueChange}
              optionFilterProp="label"
              showSearch
              mode="multiple"
            >
              {dropdownData.map(({ id: value, name: label, color }) => (
                <Select.Option key={value} value={value} label={label}>
                  {color
                    ? <BoardColorOption color={color} label={label} />
                    : label}
                </Select.Option>
              ))}
            </Select>
          );
        }
      }
      default:
        return (
          <OnTraccrTextInput
            style={{ width: 350 }}
            value={selectedValue}
            onChange={(e) => onSelectedValueChange(e.target.value)}
          />
        );
    }
  }, [
    selectedOperator,
    selectedField,
    selectedValue,
    selectedFieldValue,
    getDropdownData,
    onSelectedValueChange,
    analyticsEnabled,
    fixedDateInput,
    canUseRelativeDate,
  ]);

  const onFieldChange = (newField) => {
    setSelectedField(newField);
    setSelectedValue(null);
    setSelectedRelativeDate(undefined);
    onChange({
      field: newField,
      operator: null,
      value: null,
      type: selectedType,
      analytics: null,
    });
  };

  const onAnalyticsChange = useCallback((checked) => {
    onChange({
      ...conditionalRenderingFormula,
      analytics: checked,
      operator: null,
      value: null,
    });
  }, [onChange, conditionalRenderingFormula]);

  return (
    <>
      <Row gutter={16} style={{ paddingTop: 15 }}>
        <Col>
          <Select
            style={{ width: 400 }}
            value={selectedField}
            onChange={onFieldChange}
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ width: 700 }}
          >
            {flatFields.map(({ id: fieldId, name: fieldName }) => (
              <Select.Option key={fieldId + fieldName} value={fieldId}>
                {fieldName}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Select
            style={{ width: 180 }}
            value={selectedOperator}
            onChange={onSelectedOperatorChange}
            disabled={analyticsEnabled}
          >
            {possibleOperators.map(({ value, label }) => (
              <Select.Option key={value} value={value}>
                {label}
              </Select.Option>
            ))}
          </Select>
        </Col>
        {ValueInput && (
          <Col>
            {ValueInput}
          </Col>
        )}
        {!!showDelete && (
          <Col style={{ width: 30 }}>
            <BorderlessButton
              style={{ background: isSection ? 'transparent' : 'white' }}
              iconNode={(
                <DeleteOutlined
                  style={{
                    color: Colors.ONTRACCR_RED,
                    marginLeft: 0,
                  }}
                />
              )}
              onClick={onDeleteClicked}
            />
          </Col>
        )}
      </Row>
      {canUseRelativeDate && (
        <Row gutter={[16, 10]} style={{ marginTop: 10 }}>
          <Col>
            <OnTraccrRadioGroup
              optionType="button"
              buttonStyle="solid"
              options={DATE_MODE_OPTIONS}
              value={isRelativeDate ? DATE_MODES.RELATIVE : DATE_MODES.FIXED}
              onChange={onDateModeChange}
            />
          </Col>
          <Col>
            {isRelativeDate ? (
              <RelativeDateInput
                operator={selectedOperator}
                value={selectedRelativeDate}
                evaluatedValue={selectedValue}
                fieldLabel={fieldAnchor?.label}
                hasFieldAnchor={!!fieldAnchor}
                anchorResponse={fieldAnchor?.response}
                onChange={onRelativeDateChange}
              />
            ) : fixedDateInput}
          </Col>
        </Row>
      )}
      {canIgnoreCase(selectedField, selectedOperator) && (
        <Row style={{ marginTop: 10 }}>
          <Col style={{ display: 'flex', alignItems: 'center' }}>
            <OnTraccrCheckbox
              value={isCaseInsensitive}
              onChange={onCaseInsensitiveChange}
              label="Ignore case sensitivity"
            />
          </Col>
        </Row>
      )}
      {isAdvancedSearch && currentFieldType === 'yes-no' && (
        <Row style={{ marginTop: 10 }}>
          <Col style={{ display: 'flex', alignItems: 'center' }}>
            <OnTraccrCheckbox
              value={!!conditionalRenderingFormula?.analytics}
              onChange={onAnalyticsChange}
              label="Apply Analytics"
            />
          </Col>
        </Row>
      )}
      {!!showAdd && (
        <OnTraccrButton
          type="primary"
          style={{ marginTop: 15 }}
          icon={<PlusOutlined />}
          title="Add Another Condition"
          onClick={onAddClicked}
        />
      )}
    </>
  );
}

ConditionalRenderingBuilder.propTypes = {
  onChange: PropTypes.func.isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  id: PropTypes.string,
  conditionalRenderingFormula: PropTypes.shape({
    field: PropTypes.string,
    operator: PropTypes.string,
    value: PropTypes.string,
  }),
  customers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  projects: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  users: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  costcodes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  phases: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  projectIdMap: PropTypes.shape({}),
  vendors: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  equipment: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  formTemplates: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  labels: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  })),
  buckets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    bucketTemplateId: PropTypes.string,
  })),
  scheduleCategories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  contactAddressBooks: PropTypes.shape({}),
  formStatuses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  boardStatuses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  boardColorLegend: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    color: PropTypes.string,
  })),
  showDelete: PropTypes.bool,
  showAdd: PropTypes.bool,
  onAddClicked: PropTypes.func,
  onDeleteClicked: PropTypes.func,
  isSection: PropTypes.bool,
  additionalFields: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    selectedType: PropTypes.string,
    configProps: PropTypes.shape({
      dataType: PropTypes.string,
    }),
  })),
  isAdvancedSearch: PropTypes.bool,
  materials: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  relativeDateAnchors: PropTypes.objectOf(PropTypes.shape({
    response: PropTypes.shape({
      date: PropTypes.number,
      timezone: PropTypes.string,
    }),
    label: PropTypes.string,
  })),
};

ConditionalRenderingBuilder.defaultProps = {
  sections: [],
  id: null,
  conditionalRenderingFormula: {},
  customers: [],
  projects: [],
  users: [],
  costcodes: [],
  phases: [],
  projectIdMap: {},
  vendors: [],
  equipment: [],
  formTemplates: [],
  labels: [],
  contactAddressBooks: {},
  buckets: [],
  scheduleCategories: [],
  formStatuses: [],
  boardStatuses: [],
  boardColorLegend: [],
  showDelete: false,
  showAdd: false,
  onAddClicked: null,
  onDeleteClicked: null,
  isSection: false,
  additionalFields: [],
  isAdvancedSearch: false,
  materials: [],
  relativeDateAnchors: undefined,
};
